import React, { useEffect } from 'react';

import { Paper } from '@mui/material';
import { observer } from 'mobx-react';
import { useToggle } from 'react-use';

import classes from './ScanIqSubscriptionsManagement.module.scss';
import { scanIqSubscriptionsManagementStore } from '../../../stores/entitySubscriptionsManagementStore';
import { EmptyList, LbPagination } from '../../../../shared/components/ui-components';
import { TitleFilterRow } from './TitleFilterRow/TitleFilterRow';
import { HeaderRow } from './HeaderRow/HeaderRow';
import { SubscriptionRow } from './SubscriptionRow/SubscriptionRow';
import { CreateScanIqSubscriptionModal } from '../../../components/create-subscription-modals';
import { CreateEntitySubscriptionInfo } from '../../../interfaces/entitySubscription';
import { useEntityTaxUpdate } from '../../../hooks/useEntityTaxUpdate';
import { SOLUTION_TYPE } from '../../../../shared/constants/user-info/solutionType';

type ScanIqSubscriptionsManagementProps = {
  entityId: number;
};

const ScanIqSubscriptionsManagement = observer(
  ({ entityId }: ScanIqSubscriptionsManagementProps) => {
    const [isShowCreateSubscriptionModal, toggleShowCreateSubscriptionModal] =
      useToggle(false);

    const {
      subscriptionsList,
      filterMethods,
      changeActivationStatus,
      changeDisplayToUserStatus,
      getSubscriptionsList,
      setDefaultValues,
    } = scanIqSubscriptionsManagementStore;

    useEffect(() => {
      getSubscriptionsList(entityId);

      return () => {
        setDefaultValues();
      };
    }, []);

    useEntityTaxUpdate(SOLUTION_TYPE.SCAN_IQ, () => {
      getSubscriptionsList(entityId);
    });

    const handleCreateSubscription = async (
      subscription: CreateEntitySubscriptionInfo,
    ) => {
      await scanIqSubscriptionsManagementStore.createEntitySubscription(subscription);
    };

    return (
      <Paper variant="roundedBox">
        <TitleFilterRow
          toggleShowCreateSubscriptionModal={toggleShowCreateSubscriptionModal}
        />
        <div className={classes.subscriptionTable}>
          <HeaderRow
            sortMethods={{
              getIsActive: filterMethods.getIsSortedBy,
              onSort: filterMethods.handleSort,
              nameKey: 'NAME',
              billingPeriodTypeKey: 'BILLING_PERIOD_TYPE',
            }}
          />
          {subscriptionsList.map((subscription) => (
            <SubscriptionRow
              key={subscription.id}
              subscription={subscription}
              changeActivationStatus={changeActivationStatus}
              changeDisplayToUserStatus={changeDisplayToUserStatus}
            />
          ))}
        </div>
        <LbPagination
          {...filterMethods.getPaginationProps()}
          elementsPerPage={filterMethods.elementsPerPage}
          totalElements={filterMethods.totalElements}
          variant="inBox"
        />
        <EmptyList
          show={!subscriptionsList.length}
          isSearch={filterMethods.isSearchUsed}
          description="There are no subscriptions"
          size="small"
        />
        {isShowCreateSubscriptionModal && (
          <CreateScanIqSubscriptionModal
            entityId={entityId}
            onClose={toggleShowCreateSubscriptionModal}
            onSubmit={handleCreateSubscription}
          />
        )}
      </Paper>
    );
  },
);

export { ScanIqSubscriptionsManagement };
